import { mapGetters } from "vuex";

/**
 * Shared state and behaviour for the Slovenian payroll exports.
 *
 * The calling component supplies the intentionally different export options.
 */
export function createSlovenianExportHelper({ hideDetails, filenamePrefix }) {
  return {
    data() {
      return {
        loading: true,
        loadingMFS: false,
        downloading: false,
        selectedDate: this.$helpers.getMomentTZ().clone().subtract(1, "months"),
        timeframeStart: this.$helpers.getMomentTZ().clone().subtract(1, "months").startOf("month"),
        timeframeEnd: this.$helpers.getMomentTZ().clone().subtract(1, "months").endOf("month"),
        storesMap: [],
        stores: [],
        selectedStores: {},
        hiddenStores: 0,
        monthlyStatements: [],
        statementResponses: [],
        collectiveContracts: [],
        collectiveContractIds: [],
      };
    },
    computed: {
      ...mapGetters(["companyId", "externalSoftware", "storeArray", "globalFilter"]),
      loadingText() {
        return this.$t("employee.comp.dataLoading") + "...";
      },
      months() {
        return Array.from({ length: 12 }, (_, index) => ({
          value: index + 1,
          label: this.$t(`global.time.month${index}`),
        }));
      },
      headerButtons() {
        return {
          buttons: [
            {
              type: "block border-r",
              icon: "fa-refresh",
              disabled: this.loading || this.downloading,
              click: () => this.refresh(),
              tooltip: this.$t("settings.wage.reloadData"),
            },
            {
              type: "block border-r",
              icon: "fa-file-csv",
              disabled: this.loading || this.downloading || Object.keys(this.selectedStores).filter(key => this.selectedStores[key]).length === 0 || this.collectiveContractIds.length === 0,
              click: () => this.downloadCsv(),
              tooltip: this.$t("export.builder.downloadCsv"),
            },
          ],
          helperLinks: [{
            title: this.$t("export.builder.supportTitle"),
            helperText: this.$t("export.builder.supportHelperText"),
            externalUrl: "https://meindienstplan.atlassian.net/servicedesk/customer/portal/1/group/11",
            externalLabel: this.$t("settings.sync.contactSupport"),
            cancelLabel: this.$t("global.ui.close"),
          }],
        };
      },
      dateDisplay() {
        return this.months.find(month => month.value === this.timeframeStart.months() + 1).label + " " + this.timeframeStart.format("YYYY");
      },
      settingsCompanyState() {
        const customStores = [];
        this.hiddenStores = 0;
        this.stores.forEach((store) => {
          let pushed = false;
          if (this.globalFilter && this.globalFilter.stores && this.globalFilter.companyId == this.$store.state.companyId) {
            for (const filterStore of this.globalFilter.stores) {
              if (filterStore.id == store.id && filterStore.selected) {
                customStores.push(store);
                this.storesMap[store.id].selected = true;
                pushed = true;
              }
            }
          }
          if (!pushed) {
            this.storesMap[store.id].selected = false;
            this.hiddenStores++;
          }
        });

        const state = this.$helpers.GetDataTableStateFromKeyLabelObject({
          storeId: this.$t("settings.wage.locations"),
          employeeFinishedState: this.$t("export.builder.monthEndDoneNotDone"),
        }, customStores);
        state.headerMap.storeId.width = "40%";
        state.headerMap.storeId.componentStateBuilders = [(data) => ({
          componentType: "buttonc",
          label: data.name,
          type: "occupy-container",
          class: `  ${this.selectedStores[data.id] ? "selected" : ""}`,
          tooltip: this.$t("export.builder.selectDeselect"),
          style: "height: calc(100% - 5px) !important;",
          customEvents: { click: () => {
            this.$set(this.selectedStores, data.id, !this.selectedStores[data.id]);
            this.storesMap[data.id].selected = this.selectedStores[data.id];
            this.toggleStoreLocalStorage([data.id], this.selectedStores[data.id]);
            this.somethingHasChangedWhatAffectsExportTable = true;
            this.$forceUpdate();
          } },
          styleText: { "text-align": "left", width: "100%" },
        })];
        state.headerMap.employeeFinishedState.componentStateBuilders = [
          () => ({
            componentType: "buttonc",
            label: this.$t("export.builder.monthEnd"),
            type: "occupy-container",
            style: "height: calc(100% - 5px) !important;pointer-events:none; box-shadow: inset  10px 0 0 -5px var(--bg)",
          }),
          (data) => ({
            componentType: "buttonc",
            icon: !this.monthlyStatements[data.id].loading && this.monthlyStatements[data.id].confirmed ? "fa-check" : "fa-times",
            type: "icon-overlay",
            style: "pointer-events:none;position: absolute; top: 0, left: initial !important; right: 120px !important; bottom: 0; margin: auto; transform: scale(.8)",
            styleIcon: { color: !this.monthlyStatements[data.id].loading && this.monthlyStatements[data.id].confirmed ? "var(--md)" : "var(--ml)" },
          }),
        ];
        Object.values(state.headerMap).forEach(header => (header.stylesHeader = { "justify-content": "start" }));
        state.headerMap.employeeFinishedState.stylesHeader.paddingLeft = "5px";
        state.compact = true;
        return state;
      },
    },
    created() {
      if (this.externalSoftware === 7) this.refresh();
    },
    mounted() {
      this.$helpers.GetExportSelectedDate().then((data) => {
        if (data.currentDate && this.$helpers.getMomentTZ().diff(data.currentDate, "hours") < 3) {
          this.selectedDate = this.$helpers.getMomentFromString(data.date);
          this.timeframeStart = this.selectedDate.clone().startOf("month");
          this.timeframeEnd = this.selectedDate.clone().endOf("month");
          this.$forceUpdate();
        }
      });
    },
    methods: {
      refresh() {
        this.loading = true;
        this.loadingMFS = true;
        Promise.all([this.loadStoresForCompany(), this.loadCollectiveContracts()])
          .catch(() => this.$helpers.error(this.$t("settings.ui.errorTitle"), this.$t("settings.ui.errorLoadingData")))
          .finally(() => {
            this.loading = false;
            this.loadAllMonthlyStatements();
          });
      },
      toggleSelection(selection, id) {
        const index = selection.indexOf(id);
        if (index === -1) selection.push(id);
        else selection.splice(index, 1);
      },
      downloadCsv() {
        this.downloading = true;
        const baseUrl = "/api/sec/slovenianPayrollExport";
        let url = this.$helpers.appendParam("companyId", this.companyId, baseUrl, baseUrl);
        if (hideDetails) url = this.$helpers.appendParam("hideDetails", true, url, baseUrl);
        url = this.$helpers.appendParam("year", this.selectedDate.year(), url, baseUrl);
        url = this.$helpers.appendParam("month", this.selectedDate.month() + 1, url, baseUrl);
        url = this.$helpers.appendParam("storeIds", JSON.stringify(Object.keys(this.selectedStores).filter(key => this.selectedStores[key]).map(key => parseInt(key))), url, baseUrl);
        url = this.$helpers.appendParam("collectiveContractIds", JSON.stringify(this.collectiveContractIds), url, baseUrl);
        this.axios({ method: "get", url, responseType: "blob", headers: { AUTHORIZATION: "Bearer " + this.$store.state.jwt } })
          .then((response) => {
            const bom = "\uFEFF";
            const blobUrl = window.URL.createObjectURL(new Blob([bom, response.data], { type: "text/csv;charset=utf-8" }));
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${filenamePrefix}-payroll-${this.selectedDate.year()}-${String(this.selectedDate.month() + 1).padStart(2, "0")}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          })
          .catch(() => this.$helpers.error(this.$t("settings.ui.errorTitle"), this.$t("settings.ui.errorCreatingDocument")))
          .finally(() => { this.downloading = false; });
      },
      modDate(method) {
        if (method == "add") this.selectedDate.add(1, "months");
        else if (method == "sub") this.selectedDate.subtract(1, "months");
        this.timeframeStart = this.selectedDate.clone().startOf("month");
        this.timeframeEnd = this.selectedDate.clone().endOf("month");
        this.$forceUpdate();
        this.$helpers.SetExportSelectedDate({ currentDate: this.$helpers.getMomentTZ(), date: this.selectedDate });
        this.refresh(false, true);
      },
      loadStoresForCompany() {
        return this.$helpers.GetExportDeselectedStores().then((deselectedStores) => this.$helpers.GetCall("/api/sec/stores", {}).then((response) => {
          this.stores = response.data.data;
          this.storesMap = {};
          this.stores.forEach((store) => {
            const selected = deselectedStores[store.id] == undefined || deselectedStores[store.id] == null;
            this.$set(this.selectedStores, store.id, selected);
            this.storesMap[store.id] = { id: store.id, name: store.name, description: store.description, selected, monthlyFinancialStatement: 0 };
            this.monthlyStatements[store.id] = { confirmed: false, loading: true };
          });
          this.$forceUpdate();
        }).catch((error) => console.log(error)));
      },
      loadAllMonthlyStatements() {
        this.statementResponses = [];
        return Promise.all(this.stores.map(store => this.loadMonthlyStatementFromMD(store.id))).finally(() => {
          this.loadingMFS = false;
          this.$forceUpdate();
        });
      },
      loadMonthlyStatementFromMD(storeId) {
        return this.$helpers.GetCall("/api/sec/proxy/md/monthlyfinancialstatements", { storeId, date: this.selectedDate.format("YYYY-MM-DD") }).then((response) => {
          const statement = response.data;
          const monthlyStatement = this.monthlyStatements[storeId];
          this.statementResponses.push(statement);
          monthlyStatement.loading = false;
          monthlyStatement.confirmed = statement.length > 0 && statement.every(entry => entry.confirmed);
          this.$set(this.monthlyStatements, storeId, monthlyStatement);
        }).catch((error) => { console.log(error); throw error; });
      },
      loadCollectiveContracts() {
        return Promise.all([
          this.$helpers.GetCall("/api/sec/proxy/md/collectiveContracts", {}),
          this.$helpers.GetExportSelectedCollectiveContractsSlovenianExport(filenamePrefix),
          this.$helpers.GetStoresSettings(true).then((response) => {
            const stores = response.data;
            return Promise.all(stores.map(store => this.$helpers.GetCall("/api/sec/proxy/md/allowedContracts", { storeId: store.id })))
              .then((responses) => ({ stores, responses }));
          }),
        ]).then(([response, selectedIds, { stores, responses }]) => {
          const storeCountries = new Set(stores.map(store => store.country));
          const usedCollectiveContractIds = new Set();
          responses.forEach(({ data }) => {
            data.forEach((contract) => {
              if (contract.active) usedCollectiveContractIds.add(contract.collectiveContractId);
            });
          });
          this.collectiveContracts = response.data
            .filter(contract => usedCollectiveContractIds.has(contract.id) && storeCountries.has(contract.country))
            .sort((a, b) => a.name.localeCompare(b.name));
          this.collectiveContractIds = selectedIds.filter(id => this.collectiveContracts.some(contract => contract.id === id));
        }).catch((error) => {
          this.collectiveContracts = [];
          this.collectiveContractIds = [];
          throw error;
        });
      },
      toggleCollectiveContract(id) {
        this.toggleSelection(this.collectiveContractIds, id);
        this.$helpers.SetExportSelectedCollectiveContractsSlovenianExport(filenamePrefix, this.collectiveContractIds);
      },
      toggleStoreLocalStorage(storeIdArray, selected) {
        return this.$helpers.GetExportDeselectedStores().then((data) => {
          storeIdArray.forEach((storeId) => {
            data[storeId] = selected;
            if (selected) delete data[storeId];
          });
          return this.$helpers.SetExportDeselectedStores(data);
        });
      },
    },
  };
}
